import { Alert } from 'antd';
import { FC, ReactNode, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';

import { useBreadcrumbs } from 'src/features/layout/hooks/useBreadcrumbs';

import { BannerStepFooter } from '../components/BannerStepFooter';
import { BannerStepHeader } from '../components/BannerStepHeader';
import { BannerConfigurationStep } from '../components/steps/BannerConfigurationStep';
import { BannerContentStep } from '../components/steps/BannerContentStep';
import { BannerScheduleStep } from '../components/steps/BannerScheduleStep';
import { useBannerForm } from '../hooks/useBannerForm';

export const CreateOrUpdateBannerScreen: FC = () => {
  const {
    bannerId,
    bannerFormMethods,
    currentStep,
    handleChangeStep,
    handleSaveBannerDraft,
    handleNext,
    handleBack,
    showUnsavedAlert,
    isLoading,
    canPublish,
    stepsCompleted,
    stepsWithErrors,
    isReadOnly,
  } = useBannerForm();

  const STEP_KEYS = ['configuration', 'content', 'schedule'] as const;
  const isLastStep = currentStep === 2;
  const isCurrentStepCompleted =
    stepsCompleted[STEP_KEYS[currentStep as 0 | 1 | 2]];
  const isPublishActionDisabled = isLastStep
    ? !canPublish
    : !isCurrentStepCompleted;

  useBreadcrumbs([
    {
      label: 'Loja',
    },
    {
      label: 'Banners',
    },
    {
      label: 'Gerenciamento',
      route: '/marketplace/banners/gerenciamento',
    },
    {
      label: isReadOnly
        ? 'Visualizar Banner'
        : bannerId
          ? 'Editar Banner'
          : 'Novo Banner',
    },
  ]);

  const renderStep = useMemo(() => {
    const steps: Record<number, ReactNode> = {
      0: <BannerConfigurationStep />,
      1: <BannerContentStep />,
      2: <BannerScheduleStep />,
    };
    return steps[currentStep] || null;
  }, [currentStep]);

  const handleNavigateBack = () => {
    handleBack();
  };

  return (
    <FormProvider {...bannerFormMethods}>
      <BannerStepHeader
        currentStep={currentStep}
        onChangeStep={handleChangeStep}
        title={isReadOnly ? 'Visualizar Banner' : 'Adicionar Banner'}
        subtitle={
          isReadOnly
            ? 'Banner ativo ou agendado — desative para editar'
            : 'Defina as configurações de exibição'
        }
        backToPath={handleNavigateBack}
        stepsCompleted={stepsCompleted}
        stepsWithErrors={stepsWithErrors}
      />

      {isReadOnly && (
        <Alert
          type='warning'
          message='Este banner está ativo ou agendado. Para editar, desative-o primeiro na tela de gerenciamento.'
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {showUnsavedAlert && !isReadOnly && (
        <Alert
          type='info'
          message='Você tem alterações não salvas. Clique em "Guardar Rascunho" para não perder o progresso.'
          showIcon
          closable
          style={{ marginBottom: 24 }}
        />
      )}

      <fieldset
        disabled={isReadOnly}
        style={{ border: 'none', padding: 0, margin: 0 }}
      >
        <div
          style={{
            paddingBottom: isReadOnly ? 0 : 72,
            position: 'relative',
            ...(isReadOnly && { pointerEvents: 'none', userSelect: 'none' }),
          }}
        >
          {renderStep}
          {!isReadOnly && (
            <BannerStepFooter
              currentStep={currentStep}
              onSaveDraft={handleSaveBannerDraft}
              onNext={handleNext}
              isLoading={isLoading}
              isPrimaryActionDisabled={isPublishActionDisabled}
            />
          )}
        </div>
      </fieldset>
    </FormProvider>
  );
};
